import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Check,
  Megaphone, Users, TrendingUp, Heart,
  HandHeart
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Triple chevron arrows matching 8-pager brand element
const TripleChevron = ({ color = 'currentColor', size = 20, className = '' }) => (
  <svg
    width={size * 1.4}
    height={size}
    viewBox="0 0 28 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ flexShrink: 0 }}
  >
    <path d="M2 2L10 10L2 18" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
    <path d="M10 2L18 10L10 18" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    <path d="M18 2L26 10L18 18" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
  </svg>
);

// Scroll-triggered fade-up animation hook
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show immediately when reduced motion is preferred, or when IntersectionObserver
    // is unavailable (older Android) — matches the flagship pages' reduced-motion behavior.
    if (typeof IntersectionObserver === 'undefined' ||
        (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Animated section wrapper
const RevealSection = ({ children, className = '', delay = 0, style = {} }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
      }}
    >
      {children}
    </div>
  );
};

export default function Pledge() {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    zip: '',
    organization: '',
    sector: '',
    signerType: '',
    agreedToPledge: false,
    allowNameDisplay: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);
  const pledgeRef = useRef(null);

  const sectorOptions = [
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'business', label: 'Business / Employer' },
    { value: 'financial', label: 'Financial Institution' },
    { value: 'community', label: 'Community Organization' },
    { value: 'philanthropy', label: 'Philanthropy / Foundation' },
    { value: 'public_sector', label: 'Public Sector' }
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your first name';
    if (!formData.lastName.trim()) newErrors.lastName = 'Please enter your last name';
    if (!formData.email.trim()) newErrors.email = 'Please enter your email';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.zip.trim()) newErrors.zip = 'Please enter your ZIP code';
    if (!formData.signerType) newErrors.signerType = 'Please select how you are signing';
    if (formData.signerType === 'organizational' && !formData.sector) newErrors.sector = 'Please select your sector';
    if (!formData.agreedToPledge) newErrors.agreedToPledge = 'Please agree to the pledge to continue';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      let recaptchaToken = null;
      if (recaptchaRef.current) {
        try {
          recaptchaToken = await recaptchaRef.current.executeAsync();
          recaptchaRef.current.reset();
        } catch (err) {
          console.log('reCAPTCHA not available');
        }
      }

      const sectorLabel = sectorOptions.find(s => s.value === formData.sector)?.label || '';

      const submitData = {
        name: `${formData.name} ${formData.lastName}`,
        email: formData.email,
        organization: formData.organization || '',
        role: formData.signerType === 'organizational'
          ? `Organizational Leader - ${sectorLabel}`
          : 'Individual Leader',
        pledgeType: 'Home Library Pledge - Full Agreement',
        zip: formData.zip,
        allowNameDisplay: formData.allowNameDisplay,
        recaptchaToken
      };

      await axios.post(`${API}/pledge`, submitData);
      setSubmitted(true);
      toast.success('Thank you for taking the pledge!');
    } catch (error) {
      console.error('Pledge submission failed:', error);
      toast.error('Something went wrong. Please try again or email us at info@thehomelibraryproject.org');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToPledge = () => {
    pledgeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen">

      {/* ═══════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#2c5570' }}>
        <div className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ backgroundImage: 'url(/bg-pledge-hero.jpg)', backgroundPosition: '70% center' }} />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, #2c5570 25%, rgba(44,85,112,0.85) 50%, rgba(44,85,112,0.5) 75%, rgba(44,85,112,0.3) 100%)' }} />
        <div className="absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative mx-auto max-w-4xl px-4 py-20 sm:py-28 lg:py-32 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
            Together, we can build{' '}
            <span className="text-[#fad53a]">home libraries</span>{' '}
            for every child in Delaware.
          </h1>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold text-lg px-8"
              onClick={scrollToPledge} data-testid="hero-read-pledge-button">
              Read the Pledge
              <TripleChevron color="#1a1a1a" size={18} className="ml-2" />
            </Button>
            <Button size="lg" className="rounded-[50px] bg-white/10 border border-white/25 text-white hover:bg-white/20 font-semibold"
              onClick={scrollToForm} data-testid="hero-sign-pledge-button">
              Sign the Pledge
              <TripleChevron color="#ffffff" size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          BECOME A CHANGEMAKER
      ═══════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 rounded-full" style={{ backgroundColor: '#14919f' }} />
        <RevealSection className="mx-auto max-w-3xl px-4 sm:px-6 text-center">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#14919f] mb-4">Become a Changemaker</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2c5570] leading-tight">
            Because Book Ownership<br className="hidden sm:block" /> Changes Everything
          </h2>
          <p className="mt-6 text-lg text-[#4a4a4a] leading-relaxed">
            Join the Home Library Pledge and stand with a growing community of leaders, educators, parents,
            and organizations who believe that every child in Delaware deserves to grow up with books at home.
          </p>
          <Button className="mt-8 rounded-[50px] bg-[#14919f] hover:bg-[#117a86] text-white font-bold px-8" onClick={scrollToPledge}>
            Read the Full Pledge
            <TripleChevron color="#ffffff" size={16} className="ml-2" />
          </Button>
        </RevealSection>
      </section>

      {/* ═══════════════════════════════════════════════════
          THE PLEDGE DECLARATION
      ═══════════════════════════════════════════════════ */}
      <section ref={pledgeRef} id="pledge-text" className="relative py-16 sm:py-24 bg-cover bg-top" style={{ backgroundImage: "url('/bg-pledge-declaration.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c5570]/85 to-[#14919f]/85"></div>
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
          <RevealSection>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ border: '1px solid rgba(20,145,159,0.1)' }}>
              {/* Header with gold accent line */}
              <div className="relative px-5 sm:px-12 py-8" style={{ backgroundColor: '#2c5570' }}>
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(to right, #fad53a, #fcc419, #fad53a)' }} />
                <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">
                  The Home Library Pledge
                </h2>
                <p className="text-center text-white/50 text-sm mt-2 tracking-wider uppercase">Ten Books. Every Child. Every Year.</p>
              </div>

              {/* Body */}
              <div className="px-5 sm:px-12 py-10 sm:py-12">
                <div className="w-16 h-1 mx-auto mb-10 rounded" style={{ backgroundColor: '#14919f' }} />

                <div className="space-y-6 text-[#333] text-base sm:text-lg leading-relaxed">
                  <p>We believe that every child in Delaware deserves to grow up with books at home.</p>
                  <p>Not occasional access. Not one-time distribution. <strong className="text-[#2c5570]">Ownership.</strong></p>
                  <p>We believe that literacy does not begin in school. It begins in the home, through early language, shared reading, and everyday exposure to books.</p>

                  <div className="border-t border-gray-200 my-8" />

                  <p className="font-semibold text-[#2c5570]">We affirm a shared commitment to building a future in which:</p>
                  <ul className="space-y-4 pl-1">
                    {[
                      'Every child, from birth through eighth grade, grows a personal home library each year,',
                      'Home book ownership is treated as essential infrastructure and reaches families through the systems they already trust, including early care and education, healthcare, schools, and community organizations, and',
                      'Children are supported consistently as they move across early childhood, elementary, and middle school years.'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full mt-2.5" style={{ backgroundColor: '#14919f' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="border-t border-gray-200 my-8" />

                  <p>We recognize that building home libraries is also an investment in Delaware's future workforce and long-term economic mobility for families and communities.</p>
                  <p className="font-semibold text-[#2c5570]">We believe that strengthening home libraries is one of the most practical, evidence-informed ways to improve:</p>
                  <ul className="space-y-3 pl-1">
                    {[
                      'Early learning and school readiness,',
                      'Long-term educational outcomes,',
                      'Workforce readiness, and',
                      'The health and stability of our communities.'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full mt-2.5" style={{ backgroundColor: '#fad53a' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="border-t border-gray-200 my-8" />

                  <p>By signing this pledge, we affirm our support for a statewide, cross-sector approach to home book ownership and our shared responsibility to help make it possible.</p>

                  {/* Signature-style closing */}
                  <div className="mt-10 pt-8 border-t-2 border-[#2c5570]/10 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-[#2c5570]">
                      Book ownership changes everything.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <TripleChevron color="#fad53a" size={28} />
                    </div>
                  </div>
                </div>

                <div className="mt-10 text-center">
                  <Button size="lg" className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold text-lg px-10"
                    onClick={scrollToForm} data-testid="pledge-sign-button">
                    Sign the Pledge
                    <TripleChevron color="#1a1a1a" size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          WHY SIGN THE PLEDGE
      ═══════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <RevealSection className="text-center mb-14">
            <p className="text-sm font-semibold tracking-widest uppercase text-[#14919f] mb-4">Your Pledge, Your Impact</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#2c5570]">Why Sign the Pledge?</h2>
          </RevealSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: <Megaphone className="w-6 h-6" />, title: 'Strengthen The Movement', description: 'Take a public stand for children and literacy. Every signature adds weight to the case that Delaware is ready to lead.', color: '#14919f' },
              { icon: <Users className="w-6 h-6" />, title: 'Join A Growing Community', description: 'Connect with changemakers across sectors who share your commitment to ensuring every child has books of their own.', color: '#2c5570' },
              { icon: <TrendingUp className="w-6 h-6" />, title: 'Shape The Future', description: 'Your pledge informs state leaders, philanthropic partners, and community organizations that cross-sector support is growing.', color: '#b63435' },
              { icon: <Heart className="w-6 h-6" />, title: 'Stay Connected', description: 'Receive updates as the movement grows and discover meaningful ways to help make book ownership a reality for 150,000 children.', color: '#fad53a' }
            ].map((item, index) => (
              <RevealSection key={index} delay={index * 0.1}>
                <div className="relative rounded-xl p-6 bg-white border border-gray-100 shadow-sm hover:shadow-lg active:shadow-lg hover:-translate-y-0.5 active:-translate-y-0.5 overflow-hidden"
                  style={{ transition: 'box-shadow 0.3s ease, transform 0.3s ease' }}>
                  <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: item.color }} />
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${item.color}12`, color: item.color }}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2c5570] text-lg">{item.title}</h3>
                      <p className="mt-2 text-[#4a4a4a] leading-relaxed text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SIGN THE PLEDGE FORM
      ═══════════════════════════════════════════════════ */}
      <section ref={formRef} id="sign-the-pledge" className="relative py-16 sm:py-20" style={{ backgroundColor: '#2c5570' }}>
        <div className="absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '18px 18px', pointerEvents: 'none' }} />

        <div className="mx-auto max-w-2xl px-4 sm:px-6 relative">

          {submitted ? (
            <RevealSection>
              <div className="bg-white rounded-2xl p-10 sm:p-12 text-center shadow-2xl">
                <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                  style={{ backgroundColor: '#14919f' }}>
                  <Check className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#2c5570] mb-4">
                  Thank You for Taking the Pledge!
                </h2>
                <p className="text-[#4a4a4a] leading-relaxed mb-3">
                  You have joined a growing community of changemakers helping redefine what it means to grow up in Delaware.
                </p>
                <p className="text-[#4a4a4a] leading-relaxed mb-8">
                  We will be in touch with updates on how the movement is growing and ways you can help make
                  book ownership a reality for 150,000 children.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/donate">
                    <Button className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold px-8">
                      Donate Now
                      <TripleChevron color="#1a1a1a" size={16} className="ml-2" />
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="rounded-[50px]">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </div>
            </RevealSection>
          ) : (
            <>
              <RevealSection className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  Ignite Change. <span className="text-[#fad53a]">Get Involved.</span>
                </h2>
                <p className="mt-3 text-white/70 leading-relaxed">
                  Sign the Home Library Pledge and join a growing community of changemakers helping redefine what it means to grow up in Delaware.
                </p>
              </RevealSection>

              <RevealSection delay={0.15}>
                <div className="bg-white rounded-2xl p-5 sm:p-10 shadow-2xl" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>

                  {/* Signer commitment */}
                  <div className="mb-8 p-5 rounded-xl relative overflow-hidden" style={{ backgroundColor: '#f6f7f9' }}>
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: '#14919f' }} />
                    <p className="text-sm font-bold text-[#2c5570] uppercase tracking-wider mb-2">Signer Commitment</p>
                    <p className="text-[#4a4a4a] text-sm leading-relaxed">
                      As a signer, I commit to supporting and advancing a statewide vision for home book ownership in Delaware.
                      By signing, I join a growing community of changemakers helping redefine what it means to grow up in Delaware.
                    </p>
                  </div>

                  <div className="space-y-5">

                    {/* I am signing as */}
                    <div>
                      <label className="block text-sm font-semibold text-[#2c5570] mb-3">I am signing as: *</label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { value: 'individual', label: 'Individual Leader' },
                          { value: 'organizational', label: 'Organizational Leader' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setFormData({...formData, signerType: opt.value, sector: ''});
                              setErrors({...errors, signerType: ''});
                            }}
                            className="p-3 rounded-lg border text-sm font-medium text-center"
                            style={{
                              borderColor: formData.signerType === opt.value ? '#14919f' : '#e5e7eb',
                              backgroundColor: formData.signerType === opt.value ? '#f0fafa' : '#fff',
                              color: formData.signerType === opt.value ? '#14919f' : '#4a4a4a',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 rounded-sm border flex items-center justify-center"
                                style={{
                                  borderColor: formData.signerType === opt.value ? '#14919f' : '#d1d5db',
                                  backgroundColor: formData.signerType === opt.value ? '#14919f' : '#fff'
                                }}>
                                {formData.signerType === opt.value && <Check className="w-3 h-3 text-white" />}
                              </div>
                              {opt.label}
                            </div>
                          </button>
                        ))}
                      </div>
                      {errors.signerType && <p className="text-red-500 text-xs mt-1">{errors.signerType}</p>}
                    </div>

                    {/* Sector (conditional) */}
                    {formData.signerType === 'organizational' && (
                      <div>
                        <label className="block text-sm font-semibold text-[#2c5570] mb-2">Sector *</label>
                        <div className="flex flex-wrap gap-2">
                          {sectorOptions.map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => { setFormData({...formData, sector: opt.value}); setErrors({...errors, sector: ''}); }}
                              className="px-4 py-2 rounded-full text-sm font-medium border"
                              style={{
                                borderColor: formData.sector === opt.value ? '#14919f' : '#e5e7eb',
                                backgroundColor: formData.sector === opt.value ? '#14919f' : '#fff',
                                color: formData.sector === opt.value ? '#fff' : '#4a4a4a',
                                transition: 'all 0.15s ease',
                                minHeight: '44px'
                              }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {errors.sector && <p className="text-red-500 text-xs mt-1">{errors.sector}</p>}
                      </div>
                    )}

                    {/* Organization name (conditional) */}
                    {formData.signerType === 'organizational' && (
                      <div>
                        <label className="block text-sm font-semibold text-[#2c5570] mb-1.5">Organization Name</label>
                        <Input type="text" placeholder="Your organization" value={formData.organization}
                          onChange={e => setFormData({...formData, organization: e.target.value})} className="rounded-lg text-base" />
                      </div>
                    )}

                    {/* Name fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#2c5570] mb-1.5">First Name *</label>
                        <Input type="text" placeholder="First name" value={formData.name}
                          onChange={e => { setFormData({...formData, name: e.target.value}); setErrors({...errors, name: ''}); }}
                          className={`rounded-lg text-base ${errors.name ? 'border-red-500' : ''}`} />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#2c5570] mb-1.5">Last Name *</label>
                        <Input type="text" placeholder="Last name" value={formData.lastName}
                          onChange={e => { setFormData({...formData, lastName: e.target.value}); setErrors({...errors, lastName: ''}); }}
                          className={`rounded-lg text-base ${errors.lastName ? 'border-red-500' : ''}`} />
                        {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                      </div>
                    </div>

                    {/* Email and ZIP */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-[#2c5570] mb-1.5">Email *</label>
                        <Input type="email" placeholder="you@example.com" value={formData.email}
                          onChange={e => { setFormData({...formData, email: e.target.value}); setErrors({...errors, email: ''}); }}
                          className={`rounded-lg text-base ${errors.email ? 'border-red-500' : ''}`} />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#2c5570] mb-1.5">ZIP Code *</label>
                        <Input type="text" placeholder="19801" value={formData.zip}
                          onChange={e => { setFormData({...formData, zip: e.target.value}); setErrors({...errors, zip: ''}); }}
                          className={`rounded-lg text-base ${errors.zip ? 'border-red-500' : ''}`} maxLength={10} />
                        {errors.zip && <p className="text-red-500 text-xs mt-1">{errors.zip}</p>}
                      </div>
                    </div>

                    {/* Agreement */}
                    <div className="mt-2">
                      <button type="button" onClick={() => { setFormData({...formData, agreedToPledge: !formData.agreedToPledge}); setErrors({...errors, agreedToPledge: ''}); }}
                        className="flex items-start gap-3 w-full text-left">
                        <div className="flex-shrink-0 w-5 h-5 rounded border mt-0.5 flex items-center justify-center"
                          style={{
                            borderColor: errors.agreedToPledge ? '#ef4444' : formData.agreedToPledge ? '#14919f' : '#d1d5db',
                            backgroundColor: formData.agreedToPledge ? '#14919f' : '#fff',
                            transition: 'all 0.15s ease'
                          }}>
                          {formData.agreedToPledge && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm text-[#4a4a4a] leading-relaxed">I have read and agree to the Home Library Pledge.</span>
                      </button>
                      {errors.agreedToPledge && <p className="text-red-500 text-xs mt-1 ml-8">{errors.agreedToPledge}</p>}
                    </div>

                    {/* Name display permission */}
                    <div>
                      <button type="button" onClick={() => setFormData({...formData, allowNameDisplay: !formData.allowNameDisplay})}
                        className="flex items-start gap-3 w-full text-left">
                        <div className="flex-shrink-0 w-5 h-5 rounded border mt-0.5 flex items-center justify-center"
                          style={{
                            borderColor: formData.allowNameDisplay ? '#14919f' : '#d1d5db',
                            backgroundColor: formData.allowNameDisplay ? '#14919f' : '#fff',
                            transition: 'all 0.15s ease'
                          }}>
                          {formData.allowNameDisplay && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm text-[#4a4a4a] leading-relaxed">I give permission for my name to be listed on The Home Library Project website as a pledge signer.</span>
                      </button>
                    </div>

                    {/* reCAPTCHA */}
                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                      <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} size="invisible" />
                    )}

                    {/* Submit */}
                    <Button onClick={handleSubmit} disabled={loading}
                      className="w-full rounded-[50px] bg-[#14919f] hover:bg-[#117a86] text-white font-bold text-lg py-6 shadow-lg mt-2"
                      style={{ transition: 'background-color 0.2s ease' }} data-testid="pledge-submit-button">
                      {loading ? 'Submitting...' : (
                        <>
                          I Take the Pledge
                          <TripleChevron color="#ffffff" size={18} className="ml-3" />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-gray-400 leading-relaxed">
                      By signing, you agree to receive updates from The Home Library Project. We respect your privacy and will never share your information.
                    </p>
                  </div>
                </div>
              </RevealSection>
            </>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          READY TO TAKE THE NEXT STEP
      ═══════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-white relative">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <RevealSection className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#2c5570]">Ready to take the next step?</h2>
            <p className="mt-4 text-lg text-[#4a4a4a] max-w-2xl mx-auto leading-relaxed">
              A vision this bold requires equally as bold investments and supporters.
            </p>
          </RevealSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Organizations */}
            <RevealSection delay={0.1}>
              <div className="relative rounded-2xl border border-gray-100 bg-white shadow-md hover:shadow-xl active:shadow-xl hover:-translate-y-1 active:-translate-y-1 p-8 text-center overflow-hidden"
                style={{ transition: 'box-shadow 0.3s ease, transform 0.3s ease' }}>
                <div className="h-1.5 absolute top-0 left-0 right-0" style={{ backgroundColor: '#2c5570' }} />
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
                  style={{ backgroundColor: 'rgba(44,85,112,0.1)', color: '#2c5570' }}>
                  <Users className="w-7 h-7" />
                </div>
                <p className="text-sm font-semibold tracking-widest uppercase text-[#14919f] mb-2">Organizations</p>
                <h3 className="text-lg font-bold text-[#2c5570] mb-4">Explore Our Founding Partner Opportunities</h3>
                <Link to="/founders-circle">
                  <Button className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold px-8">
                    Learn More
                    <TripleChevron color="#1a1a1a" size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </RevealSection>

            {/* Individuals */}
            <RevealSection delay={0.2}>
              <div className="relative rounded-2xl border border-gray-100 bg-white shadow-md hover:shadow-xl active:shadow-xl hover:-translate-y-1 active:-translate-y-1 p-8 text-center overflow-hidden"
                style={{ transition: 'box-shadow 0.3s ease, transform 0.3s ease' }}>
                <div className="h-1.5 absolute top-0 left-0 right-0" style={{ backgroundColor: '#14919f' }} />
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
                  style={{ backgroundColor: 'rgba(20,145,159,0.1)', color: '#14919f' }}>
                  <HandHeart className="w-7 h-7" />
                </div>
                <p className="text-sm font-semibold tracking-widest uppercase text-[#2c5570] mb-2">Individuals</p>
                <h3 className="text-lg font-bold text-[#2c5570] mb-4">Make a One-Time or Recurring Donation</h3>
                <Link to="/donate">
                  <Button className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold px-8">
                    Donate Now
                    <TripleChevron color="#1a1a1a" size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </RevealSection>
          </div>

          {/* Contact option */}
          <RevealSection delay={0.3} className="mt-10 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 text-[#14919f] hover:text-[#117a86] font-semibold text-lg group"
              style={{ transition: 'color 0.2s ease' }}>
              Have questions?{' '}
              <span className="underline underline-offset-4 decoration-[#14919f]/40 group-hover:decoration-[#14919f]">Contact Us</span>
              <TripleChevron color="#14919f" size={16} />
            </Link>
          </RevealSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          FINAL CTA. With About page hero background
      ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-cover bg-top" style={{ backgroundImage: "url('/bg-about-hero.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c5570]/90 to-[#14919f]/90"></div>
        <div className="absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '16px 16px' }} />
        <div className="relative mx-auto max-w-3xl px-4 py-16 sm:py-20 text-center">
          <RevealSection>
            <p className="text-2xl sm:text-3xl font-bold text-[#fad53a] leading-snug">
              Together, we commit to making Delaware the first state where every child grows up owning books at home.
            </p>
          </RevealSection>
        </div>
      </section>

    </div>
  );
}
